import React, { useState, useMemo } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getCommunityItems, deleteCommunityPost } from "wasp/client/operations";
import type { CommunitySectionConfig } from "../types";
import { COMMUNITY_SECTIONS as SHARED_SECTIONS } from "../constants";
import { useAuth } from "wasp/client/auth";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../client/components/Sidebar";
import { useLocationContext } from "../../../client/contexts/LocationContext";
import {
  EnhancedPageLayout,
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
} from "../../../components/ui/enhanced-page-layout";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";
import {
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  AlertCircle,
  MessageSquare,
  Users,
  Grid3X3,
  List,
  Table,
} from "lucide-react";

const COMMUNITY_SECTIONS_MAP = Object.fromEntries(
  SHARED_SECTIONS.map((s) => [s.path, s])
);

export default function CommunityListPage({ section: propSection }: { section?: CommunitySectionConfig }) {
  const { sectionKey } = useParams<{ sectionKey: string }>();
  const section = propSection ||
    COMMUNITY_SECTIONS_MAP[sectionKey as string] || SHARED_SECTIONS[1]; // Fallback to Ask for Help
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const deleteItemFn = useAction(deleteCommunityPost);

  // State for filters and view mode
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "title">("newest");

  // Use responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("table");

  // Fetch items
  const {
    data: items = [],
    isLoading,
    error,
  } = useQuery(getCommunityItems, {
    locationId: selectedLocation?.id || "",
    contentType: (section?.contentType || "ASK_FOR_HELP") as any,
  });

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = items as any[];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (item: any) =>
          item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Sort
    switch (sortBy) {
      case "newest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case "oldest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
      case "title":
        filtered = [...filtered].sort((a: any, b: any) =>
          a.title.localeCompare(b.title),
        );
        break;
    }

    return filtered;
  }, [items, searchTerm, sortBy]);

  const handleDelete = async (itemId: string) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await deleteItemFn({ itemId });
      } catch (error) {
        console.error("Error deleting item:", error);
      }
    }
  };

  if (!section) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center">Section not found</div>
      </DashboardLayout>
    );
  }

  const effectiveKey = section?.path || sectionKey || "";
  const addPath = selectedLocation?.locationCode
    ? `/locations/${selectedLocation.locationCode}/${effectiveKey}/add`
    : `/${effectiveKey}/add`;

  const headerContent = (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{section.label}</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            {section.description} at {selectedLocation?.name || "Location"} (
            {filteredItems.length} items)
          </p>
          {section.contentType === "URGENT_HELP" && (
            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-red-50 border border-red-200 rounded-full text-[10px] font-semibold text-red-700">
              <AlertCircle className="w-3 h-3" />
              For real emergencies, call 911 immediately.
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <EnhancedActionButton
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            icon={Filter}
            label=""
            variant={isFilterExpanded ? "primary" : "secondary"}
            className="hidden lg:flex"
          />

          <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
            <EnhancedActionButton
              onClick={() => setIsFilterExpanded(true)}
              icon={Filter}
              label=""
              variant={isFilterExpanded ? "primary" : "secondary"}
              className="lg:hidden"
            />
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="py-6 space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Search</label>
                  <input
                    type="text"
                    placeholder="Search community..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm"
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="title">A-Z</option>
                  </select>
                </div>
                <Button className="w-full" onClick={() => setIsFilterExpanded(false)}>Apply</Button>
              </div>
            </SheetContent>
          </Sheet>
          {user?.isAdmin && (
            <EnhancedActionButton
              onClick={() => navigate(addPath)}
              icon={Plus}
              label={<span className="hidden sm:inline">Post</span>}
            />
          )}
        </div>
      </div>
    </div>
  );

  const filtersContent = (
    <div 
      className={cn(
        "bg-sidebar-hover border-t border-border transition-all duration-300 overflow-hidden hidden lg:block",
        isFilterExpanded ? "max-h-[500px]" : "max-h-0 border-none"
      )}
    >
      <div className="px-6 py-3 space-y-3">
        {viewMode === "table" && (
          <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
            <div className="col-span-5 min-w-0 truncate">Item</div>
            <div className="col-span-3 min-w-0 truncate">Author</div>
            <div className="col-span-2 min-w-0 truncate">Date</div>
            {user?.isAdmin && <div className="col-span-2 text-right">Actions</div>}
          </div>
        )}
        
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-5">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="col-span-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="title">Title A-Z</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  if (!user || !selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {!user ? "Authentication Required" : "No Location Selected"}
            </h2>
            <p className="text-gray-600">
              {!user 
                ? "You must be logged in to view community items." 
                : "Please select a location to view community items."}
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <EnhancedPageLayout
        header={headerContent}
        filters={filtersContent}
      >
        <div className="w-full">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {!isLoading && !error && filteredItems.length === 0 && (
            <div className="text-center py-12 text-gray-500">No items found in this section.</div>
          )}

          {!isLoading && !error && filteredItems.length > 0 && (
            <div className={cn(
              viewMode === "table" ? "w-full" : "p-6",
              viewMode === "cards" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"
            )}>
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className={cn(
                    viewMode === "cards" 
                      ? enhancedPageLayoutStyles.card.container 
                      : viewMode === "list" 
                        ? "bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                        : "px-6 py-4 hover:bg-sidebar-hover transition-colors border-b border-gray-100 cursor-pointer"
                  )}
                  onClick={() => {
                    const viewPath = selectedLocation?.locationCode
                      ? `/locations/${selectedLocation.locationCode}/${effectiveKey}/${item.id}`
                      : `/${effectiveKey}/${item.id}`;
                    navigate(viewPath);
                  }}
                >
                  {viewMode === "table" ? (
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-5">
                        <div className="font-medium text-gray-900 truncate">{item.title}</div>
                        <div className="text-xs text-gray-500 truncate">{item.description}</div>
                      </div>
                      <div className="col-span-3 text-sm text-gray-600 truncate">
                        {item.author.name || item.author.email}
                      </div>
                      <div className="col-span-2 text-sm text-gray-500">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </div>
                      {user?.isAdmin && (
                        <div className="col-span-2 flex justify-end gap-2" onClick={(e) => e?.stopPropagation()}>
                                    <EnhancedIconButton onClick={(e) => { 
                                      e?.stopPropagation(); 
                                      const editPath = selectedLocation?.locationCode
                                        ? `/locations/${selectedLocation.locationCode}/${effectiveKey}/${item.id}/edit`
                                        : `/${effectiveKey}/${item.id}/edit`;
                                      navigate(editPath); 
                                    }} icon={Edit} title="Edit" />
                                    <EnhancedIconButton onClick={(e) => { e?.stopPropagation(); handleDelete(item.id); }} icon={Trash2} title="Delete" variant="danger" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={enhancedPageLayoutStyles.card.content}>
                      <div className={enhancedPageLayoutStyles.card.header}>
                        <h3 className={enhancedPageLayoutStyles.card.title}>{item.title}</h3>
                      </div>
                      <p className={enhancedPageLayoutStyles.card.description}>{item.description}</p>
                      <div className={enhancedPageLayoutStyles.card.footer}>
                        <div className={enhancedPageLayoutStyles.card.meta}>
                          by {item.author.name || item.author.email} • {new Date(item.createdAt).toLocaleDateString()}
                        </div>
                        {user?.isAdmin && (
                          <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                            <EnhancedIconButton onClick={(e) => { 
                              e?.stopPropagation(); 
                              const editPath = selectedLocation?.locationCode
                                ? `/locations/${selectedLocation.locationCode}/${effectiveKey}/${item.id}/edit`
                                : `/${effectiveKey}/${item.id}/edit`;
                              navigate(editPath); 
                            }} icon={Edit} title="Edit" />
                            <EnhancedIconButton onClick={() => handleDelete(item.id)} icon={Trash2} title="Delete" variant="danger" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </EnhancedPageLayout>
    </DashboardLayout>
  );
}
